import ChevronLeft from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import Fab from "@mui/material/Fab";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import clsx from "clsx";
import type { CSSProperties, Ref } from "react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimateFlip } from "../../components/AnimateFlip";
import { ToolbarLogo } from "../../components/ToolbarLogo";
import { useElementDimensions } from "../../hooks/useElementDimensions";
import { useStoredApiToken } from "../../store/apiToken";
import { useStoredSidebarCollapsed } from "../../store/sidebar";
import { Navigation } from "../Navigation";
import { LanguageSelection } from "./LanguageSelection";
import { LogoutButton } from "./LogoutButton";
import classes from "./Sidebar.module.css";
import { ThemePicker } from "./ThemePicker";

interface SidebarContentProps {
  collapsed?: boolean;
  /** Left out by the mobile drawer, which slides in and out instead. */
  onCollapsedToggle?: () => void;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Everything both drawers show. The mobile drawer is the desktop sidebar,
 * minus the collapsing it has no room for.
 */
const SidebarContent = (props: SidebarContentProps) => {
  const { collapsed = false, onCollapsedToggle, ref } = props;
  const { t } = useTranslation();
  const [apiToken] = useStoredApiToken();
  const isValidSession = typeof apiToken !== "undefined";

  const actionTooltipPlacement = collapsed ? "right" : "top";
  const collapseSidebarLabel = String(t("Layout.collapseSidebar"));
  const expandSidebarLabel = String(t("Layout.expandSidebar"));

  return (
    <div className={classes.content} ref={ref}>
      <div className={classes.header}>
        <ToolbarLogo className={classes.logo} />
      </div>
      <Divider />
      <div className={classes.navigation}>
        {isValidSession && <Navigation collapsed={collapsed} />}
      </div>
      <AnimateFlip>
        <Divider />
      </AnimateFlip>
      <AnimateFlip>
        <div className={classes.actions}>
          <AnimateFlip>
            <LanguageSelection tooltipPlacement={actionTooltipPlacement} />
          </AnimateFlip>
          <AnimateFlip>
            <ThemePicker tooltipPlacement={actionTooltipPlacement} />
          </AnimateFlip>
          <AnimateFlip>
            <LogoutButton tooltipPlacement={actionTooltipPlacement} />
          </AnimateFlip>
        </div>
      </AnimateFlip>
      {typeof onCollapsedToggle !== "undefined" && (
        <>
          <Divider />
          <List disablePadding>
            <ListItem disablePadding>
              {/*
               * The label only ever shows while the sidebar is open, so it
               * always describes collapsing. Assistive technology gets the
               * actual action.
               */}
              <ListItemButton
                aria-label={
                  collapsed ? expandSidebarLabel : collapseSidebarLabel
                }
                className={classes.collapseButton}
                onClick={onCollapsedToggle}
              >
                {/* The tooltip wraps the rail, so that it anchors to what stays visible. */}
                <Tooltip
                  placement="right"
                  title={collapsed ? expandSidebarLabel : ""}
                >
                  <ListItemIcon className={classes.collapseIcon}>
                    <ChevronLeft className={classes.collapseChevron} />
                  </ListItemIcon>
                </Tooltip>
                <ListItemText
                  primary={collapseSidebarLabel}
                  slotProps={{ primary: { noWrap: true } }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}
    </div>
  );
};

interface SidebarProps {
  /** Placed on the docked drawer, the only one that takes up room in a layout. */
  className?: string;
}

export const Sidebar = (props: SidebarProps) => {
  const { className } = props;
  const { t } = useTranslation();
  const [apiToken] = useStoredApiToken();
  const isValidSession = typeof apiToken !== "undefined";
  const [storedCollapsed, setStoredCollapsed] = useStoredSidebarCollapsed();

  /*
   * Without a session there is no navigation to make room for, so the sidebar
   * stays collapsed. The stored preference is left alone for the next login.
   */
  const collapsed = !isValidSession || storedCollapsed;

  const handleCollapsedToggle = useCallback(() => {
    setStoredCollapsed(!storedCollapsed);
  }, [setStoredCollapsed, storedCollapsed]);

  const [mobileOpen, setMobileOpen] = useState(false);

  const handleMobileOpen = useCallback(() => {
    setMobileOpen(true);
  }, []);

  const handleMobileClose = useCallback(() => {
    setMobileOpen(false);
  }, []);

  const [content, setContent] = useState<HTMLDivElement | null>(null);
  const contentDimensions = useElementDimensions(content);

  /*
   * Measuring the content's width gives us an animatable value, since
   * `max-content` cannot be transitioned to the collapsed width. It is the one
   * value the stylesheet cannot derive on its own, so it reaches it as a
   * custom property.
   */
  const widthStyle = {
    "--sidebar-width":
      typeof contentDimensions === "undefined"
        ? "max-content"
        : `${contentDimensions.width}px`,
  } as CSSProperties;

  return (
    <>
      {/* Mobile temporary drawer */}
      <Drawer
        ModalProps={{ keepMounted: true }}
        className={classes.mobileDrawer}
        onClose={handleMobileClose}
        open={mobileOpen}
        variant="temporary"
      >
        <SidebarContent />
      </Drawer>

      {/* Opens the drawer above, and hides wherever the desktop one shows. */}
      <div className={classes.mobileFab}>
        <Fab aria-label={t("Layout.openSidebar")} onClick={handleMobileOpen}>
          <MenuIcon />
        </Fab>
      </div>

      {/* Desktop permanent sidebar */}
      <Drawer
        className={clsx(classes.sidebar, className, {
          [classes.collapsed]: collapsed,
        })}
        slotProps={{ paper: { className: classes.sidebarPaper } }}
        style={widthStyle}
        variant="permanent"
      >
        <SidebarContent
          collapsed={collapsed}
          onCollapsedToggle={isValidSession ? handleCollapsedToggle : undefined}
          ref={setContent}
        />
      </Drawer>
    </>
  );
};
